import React, { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Typography,
    Grid,
    Box,
    Stack,
    Button,
    TextField,
    MenuItem,
    Divider,
    useTheme,
} from '@mui/material';
import { Save, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import VariablesEditor from './VariablesEditor';
import FormInput from '../inputs/FormInput';
import SwitchInput from '../inputs/SwitchInput';

// NOTE: Channel + locale dropdowns and the code text input intentionally
// stay as raw MUI TextField — channel/locale are enum dropdowns (not the
// FK shape FormInput's `select` expects), and the code field uses the
// monospace inputProps style which FormInput's default text input does
// not expose.

const CHANNEL_OPTIONS = [
    { value: 'email', labelEn: 'Email', labelAr: 'بريد إلكتروني' },
    { value: 'sms',   labelEn: 'SMS',   labelAr: 'رسالة نصية' },
    { value: 'push',  labelEn: 'Push',  labelAr: 'إشعار' },
];

const LOCALE_OPTIONS = [
    { value: '',   labelEn: 'Default (any language)', labelAr: 'الافتراضي (أي لغة)' },
    { value: 'en', labelEn: 'English',                labelAr: 'الإنجليزية' },
    { value: 'ar', labelEn: 'Arabic',                 labelAr: 'العربية' },
];

const buildInitial = (template) => ({
    code: template?.code || '',
    channel: template?.channel || 'email',
    locale: template?.locale ?? '',
    subject: template?.subject || '',
    body: template?.body || '',
    variables: Array.isArray(template?.variables) ? template.variables : [],
    is_active: template?.is_active !== false,
});

/**
 * Reusable form for create + edit. On `mode==='edit'` the immutable identity
 * fields (code, channel, locale) are read-only — the backend will reject any
 * attempt to change them.
 */
const FormAddEditNotificationTemplate = ({
    mode = 'create',
    template,
    onSubmit,
    isSubmitting,
    extraSlot,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();

    const isEdit = mode === 'edit';
    const initial = useMemo(() => buildInitial(template), [template]);
    const [form, setForm] = useState(initial);

    useEffect(() => {
        setForm(initial);
    }, [initial]);

    const setField = (field) => (eOrValue) => {
        const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = isEdit
            ? {
                subject: form.channel === 'email' ? (form.subject || null) : null,
                body: form.body,
                variables: form.variables,
                is_active: form.is_active,
            }
            : {
                code: form.code.trim(),
                channel: form.channel,
                locale: form.locale || null,
                subject: form.channel === 'email' ? (form.subject || null) : null,
                body: form.body,
                variables: form.variables,
                is_active: form.is_active,
            };
        onSubmit(payload);
    };

    const handleReset = () => setForm(initial);

    const showSubject = form.channel === 'email';

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Identity card */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    mb: 3,
                }}
            >
                <Typography variant="h6" fontWeight={700} mb={2}>
                    {isArabic ? 'هوية القالب' : 'Template identity'}
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label={isArabic ? 'الكود' : 'Code'}
                            placeholder="order.placed"
                            value={form.code}
                            onChange={setField('code')}
                            disabled={isEdit || isSubmitting}
                            helperText={
                                isEdit
                                    ? (isArabic ? 'الكود غير قابل للتعديل بعد الإنشاء' : 'Code cannot be changed after creation')
                                    : (isArabic ? 'مفتاح الحدث الذي يستخدمه السيرفر — مثل order.placed' : 'Event key the server uses — e.g. order.placed')
                            }
                            inputProps={{ style: { fontFamily: 'monospace' } }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            required
                            label={isArabic ? 'القناة' : 'Channel'}
                            value={form.channel}
                            onChange={setField('channel')}
                            disabled={isEdit || isSubmitting}
                        >
                            {CHANNEL_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {isArabic ? opt.labelAr : opt.labelEn}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label={isArabic ? 'اللغة' : 'Locale'}
                            value={form.locale}
                            onChange={setField('locale')}
                            disabled={isEdit || isSubmitting}
                        >
                            {LOCALE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value || 'default'} value={opt.value}>
                                    {isArabic ? opt.labelAr : opt.labelEn}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <SwitchInput
                            label={isArabic ? 'مفعّل' : 'Active'}
                            name="is_active"
                            checked={form.is_active}
                            onChange={(e) => setField('is_active')(e.target.checked)}
                            disabled={isSubmitting}
                            helperText={
                                isArabic
                                    ? 'لو متوقف، سيرجع السيرفر للنص الافتراضي عند الإرسال.'
                                    : 'If off, the server falls back to the hardcoded default body.'
                            }
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Content card */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    mb: 3,
                }}
            >
                <Typography variant="h6" fontWeight={700} mb={2}>
                    {isArabic ? 'محتوى القالب' : 'Template content'}
                </Typography>

                <Stack spacing={2.5}>
                    {showSubject && (
                        <Box>
                            <FormInput
                                type="text"
                                name="subject"
                                label={isArabic ? 'العنوان' : 'Subject'}
                                value={form.subject}
                                onChange={setField('subject')}
                                disabled={isSubmitting}
                                required={false}
                                placeholder=""
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {isArabic
                                    ? 'سطر العنوان (للبريد الإلكتروني فقط). يمكن استخدام {{متغير}}.'
                                    : 'Email subject line. Supports {{variable}} placeholders.'}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <FormInput
                            type="textarea"
                            name="body"
                            label={isArabic ? 'النص' : 'Body'}
                            value={form.body}
                            onChange={setField('body')}
                            disabled={isSubmitting}
                            required
                            textareaRows="220px"
                            placeholder=""
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {isArabic
                                ? 'استخدم {{اسم_المتغير}} لإدراج بيانات ديناميكية.'
                                : 'Use {{variable_name}} to insert dynamic data.'}
                        </Typography>
                    </Box>

                    <Divider />

                    <VariablesEditor
                        value={form.variables}
                        onChange={setField('variables')}
                        disabled={isSubmitting}
                    />
                </Stack>
            </Paper>

            {extraSlot}

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="flex-end"
            >
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RestartAlt />}
                    onClick={handleReset}
                    disabled={isSubmitting}
                >
                    {isArabic ? 'استعادة' : 'Reset'}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={isSubmitting || !form.body || (!isEdit && !form.code)}
                >
                    {isSubmitting
                        ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                        : isEdit
                            ? (isArabic ? 'حفظ التغييرات' : 'Save changes')
                            : (isArabic ? 'إنشاء القالب' : 'Create template')}
                </Button>
            </Stack>
        </Box>
    );
};

export default FormAddEditNotificationTemplate;
